import { getMyProfile } from '../api/users';
import { registerSessionCache } from './session-caches';

/**
 * 내가 조직에서 무엇을 맡았나 · 무엇을 볼 수 있나 — **한 번만 물어보고 나눠 쓴다** (2026-08-31).
 *
 * ★★★ **왜 생겼나.** 직원이 근태를 열 때마다 서버에 **403 이 두 번** 찍히고 있었다
 *    (`/hr/attendance/company-today` · `/hr/attendance/team`). 둘 다 「담당자·팀장만 되는」
 *    라우트를 **누구에게나 찔러 보고** 되면 그리는 방식이었기 때문이다.
 *    `AttShell` 은 바로 옆 줄에서 근무제 목록에 대해 **「직원에게는 부르지 않는다(403 을
 *    만들지 않게)」** 라고 이미 적어 두었는데, 그 규칙이 이 둘에는 안 왔다.
 *
 * ★★ **찔러 보지 않고도 알 수 있다** — `GET /users/me` 가 두 칸을 준다:
 *    `orgScope`(내가 팀장·부서장·대표인가, 자리 재정의 판 5) ·
 *    **`canSeeCompanyAttendance`**(전사 근태를 볼 수 있나, 2026-08-31) ·
 *    **`isRepresentative`**(내가 대표인가, 2026-09-01 — 「업무보고」 메뉴가 쓴다).
 *    뒤엣것이 없던 동안 **근태 담당자는 메뉴가 아예 안 섰다** — 역할이 `STAFF` 라 화면이
 *    직원과 구별할 방법이 없었기 때문이다(부여를 해 줘도 갈 길이 없었다는 뜻).
 *
 * ★ **모듈 캐시**를 두는 것은 화면 둘(`AttShell` · `MyAttendancePage`)이 같은 답을 쓰기
 *   때문이다 — 종전에는 `/users/me` 도 각자 불렀다. 실패하면 «맡은 것 없음 · 못 봄»으로 본다
 *   (모르면 안 보여 주는 쪽 — 화면이 없는 길을 내지 않는다).
 */
export type MyOrgScope = 'ALL' | 'DIVISION' | 'TEAM' | 'NONE';

/** 한 번의 `/users/me` 에서 뽑아 쓰는 「내 자격」 한 벌. */
export interface MyDutyScope {
  orgScope: MyOrgScope;
  /** 전사 근태를 볼 수 있나. 옛 서버는 이 칸을 안 준다 → 거짓. */
  canSeeCompanyAttendance: boolean;
  /**
   * **내가 대표인가** (2026-09-01 · 「업무보고」 메뉴).
   *
   * ★★ 메뉴 권한(`menu_access_rules`)은 **부서 단위**뿐이라 「대표 한 사람」을 지정할 수 없다.
   *    그리고 **대표가 관리자 역할이 아닐 수 있어** 역할로 잠그면 정작 볼 사람이 못 본다.
   *    그래서 「우리 조직」·「전사 근태」와 **같은 축**으로 한 칸을 더 받는다.
   * ★ 옛 서버는 이 칸을 안 준다 → 거짓(모르면 안 보여 주는 쪽).
   */
  isRepresentative: boolean;
}

const NONE: MyDutyScope = { orgScope: 'NONE', canSeeCompanyAttendance: false, isRepresentative: false };

let cached: Promise<MyDutyScope> | null = null;

/** 내 자격 한 벌 — 요청은 **한 번**이다. */
export function getMyDutyScope(): Promise<MyDutyScope> {
  cached ??= getMyProfile()
    .then((p) => ({
      orgScope: (p.orgScope ?? 'NONE') as MyOrgScope,
      // 못 받으면(옛 서버) 거짓 — 있는 척하지 않는다.
      canSeeCompanyAttendance: p.canSeeCompanyAttendance === true,
      isRepresentative: p.isRepresentative === true,
    }))
    // 실패는 다시 물어볼 수 있게 캐시를 비운다 — 한 번의 네트워크 실패가 세션 내내 남지 않게.
    .catch(() => { cached = null; return NONE; });
  return cached;
}

/** 맡은 조직의 범위만 — 종전 부르는 자리를 그대로 둔다. */
export function getMyOrgScope(): Promise<MyOrgScope> {
  return getMyDutyScope().then((s) => s.orgScope);
}

/** 테스트 전용 — 모듈 캐시 비우기. */
export function _resetMyOrgScopeForTests(): void { cached = null; }

/**
 * ★★★ **로그아웃하면 비운다** (2026-08-31). 여기 담긴 것은 신상이 아니라 **자격**이다 —
 *    안 비우면 담당자가 로그아웃한 자리에 직원이 로그인했을 때 「근태 관리」가 그대로 선다.
 */
registerSessionCache(() => { cached = null; });
